import {
  CreateRoomRequest,
  Room,
  RoomFilters,
  RoomResponse,
  RoomWithReservationsData,
  UpdateRoomRequest,
} from "@/types/room";
import type {
  RoomUsageAnalyticsBucket,
  RoomUsageAnalyticsGroupBy,
  RoomUsageAnalyticsResponse,
  RoomUsageHistoryResponse,
} from "@/types/roomUsage";
import { BaseService } from "./BaseService";
import { IRoomService } from "./interfaces/IRoomService";

class RoomService extends BaseService implements IRoomService {
  private basePath = "/rooms";

  constructor() {
    super();
  }

  async getRooms(
    hotelId: string,
    filters?: RoomFilters,
    page?: number,
    limit?: number,
  ): Promise<RoomResponse> {
    const roomsFilter = {
      ...filters,
      page,
      limit,
    };

    return this.get<RoomResponse>(
      `/hotels/${hotelId}${this.basePath}`,
      roomsFilter,
    );
  }

  async getAvailableRooms(
    hotelId: string,
    filters?: RoomFilters,
  ): Promise<RoomResponse> {
    return this.get<RoomResponse>(
      `/hotels/${hotelId}${this.basePath}/available`,
      filters,
    );
  }

  async getRoomById(
    id: string,
    hotelId: string,
  ): Promise<RoomWithReservationsData> {
    return this.get<RoomWithReservationsData>(
      `/hotels/${hotelId}${this.basePath}/${id}`,
    );
  }

  async createRoom(roomData: CreateRoomRequest): Promise<Room> {
    const { hotelId, photoId, number, categoryId, status, area, floor } =
      roomData;

    return this.post<Room>(`/hotels/${hotelId}${this.basePath}`, {
      number,
      categoryId,
      status,
      area,
      floor,
      ...(photoId ? { photoId } : {}),
    });
  }

  async updateRoom(
    id: string,
    hotelId: string,
    roomData: UpdateRoomRequest,
  ): Promise<Room> {
    return this.put<Room>(`/hotels/${hotelId}${this.basePath}/${id}`, roomData);
  }

  async deleteRoom(id: string, hotelId: string): Promise<void> {
    return this.delete<void>(`/hotels/${hotelId}${this.basePath}/${id}`);
  }

  async getRoomUsageHistory(
    hotelId: string,
    roomId: string,
    filters: {
      from: string;
      to: string;
    },
  ): Promise<RoomUsageHistoryResponse> {
    return this.get<RoomUsageHistoryResponse>(
      `/hotels/${hotelId}${this.basePath}/${roomId}/usage-history`,
      filters,
    );
  }

  async getRoomUsageAnalytics(
    hotelId: string,
    filters: {
      from: string;
      to: string;
      groupBy: RoomUsageAnalyticsGroupBy;
      bucket?: RoomUsageAnalyticsBucket;
    },
  ): Promise<RoomUsageAnalyticsResponse> {
    return this.get<RoomUsageAnalyticsResponse>(
      `/hotels/${hotelId}${this.basePath}/usage-analytics`,
      filters,
    );
  }

  /**
   * Get rooms with centralized filtering from context
   * This method can be used when you want to apply the global room filters
   * from the RoomContext without having to pass them manually
   */
  async getRoomsWithContextFilters(
    contextFilters: RoomFilters,
  ): Promise<RoomResponse> {
    return this.getRooms("", contextFilters);
  }
}

export const roomService = new RoomService();
